import React, { useState, useEffect, useCallback, ReactNode } from "react";
import Session from "supertokens-auth-react/recipe/session";
import Passwordless from "supertokens-auth-react/recipe/passwordless";
import { AuthContext, AuthState, AuthContextType } from "./authContextDefinition";
import { authService } from "../services/authService";

export type { AuthState, AuthContextType } from "./authContextDefinition";

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    isLoading: true,
    error: null,
  });

  const checkSession = async (): Promise<boolean> => {
    try {
      // Check both SuperTokens session and anonymous token
      const sessionExists = await Session.doesSessionExist();
      const hasAnonymousToken = authService.isAnonymousUser();
      return sessionExists || hasAnonymousToken;
    } catch (error) {
      console.error("Error checking session:", error);
      setAuthState((prev: AuthState) => ({ ...prev, error: "Failed to check session" }));
      return false;
    }
  };

  const refreshAuth = useCallback(async () => {
    // Do not set isLoading here. App.tsx replaces <Routes> with a spinner whenever
    // auth isLoading is true, which unmounts the current page (including a large
    // album gallery) and remounts it after the session check.
    try {
      const sessionExists = await checkSession();
      setAuthState({
        isAuthenticated: sessionExists,
        isLoading: false,
        error: null,
      });

    } catch (e) {
      console.error("Error refreshing auth:", e);
      setAuthState({
        isAuthenticated: false,
        isLoading: false,
        error: "Authentication check failed",
      });
    }
  }, []);

  const signOut = async () => {
    try {
      // Actually sign out the user
      const isLoggedIn = await Session.doesSessionExist();

      if (isLoggedIn) {
        // Sign out from SuperTokens
        await Session.signOut();
      }

      // Best-effort: clear cached authenticated media previews to avoid leaking
      // thumbnails / previews across users on shared devices.
      try {
        if ("caches" in window) {
          await caches.delete("api-media-previews-cache");
        }
      } catch {
        // Ignore cache cleanup errors (e.g., unsupported browser, storage errors)
      }

      // Don't clear anonymous token - that's only for when anonymous users explicitly want to login
      // The anonymous token should persist through logout so users don't lose their anonymous shares
      // Mark as not authenticated
      setAuthState({
        isAuthenticated: false,
        isLoading: false,
        error: null,
      });
    } catch (e) {
      console.error("Error during logout:", e);
      setAuthState((prev: AuthState) => ({
        ...prev,
        isAuthenticated: false,
        error: "Logout failed",
      }));
    }
  };

  const handleBackNavigation = async () => {
    try {
      // Both logged-in and anonymous users should stay authenticated
      // Just refresh the auth state
      await refreshAuth();
    } catch (e) {
      console.error("Error during back navigation:", e);
      setAuthState((prev: AuthState) => ({
        ...prev,
        error: "Navigation failed",
      }));
    }
  };

  const handleMagicLink = useCallback(async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const preAuthSessionId = urlParams.get("preAuthSessionId");

    if (preAuthSessionId && window.location.pathname === "/auth/verify") {

      try {
        const response = await Passwordless.consumeCode();

        if (response.status === "OK") {

          // Check if there are anonymous shares to merge
          const hasAnonymousShares = authService.getAnonymousShareTokens().length > 0;
          if (hasAnonymousShares) {
            await authService.mergeAnonymousShares();
          }

          const sessionExists = await checkSession();

          if (sessionExists) {
            setAuthState({
              isAuthenticated: true,
              isLoading: false,
              error: null,
            });
            window.history.replaceState({}, "", "/");
          }
        }
      } catch (error) {
        console.error("Error processing magic link:", error);
        setAuthState((prev: AuthState) => ({
          ...prev,
          error: "Failed to process magic link",
          isLoading: false,
        }));
      }
    }
  }, []);

  // Initial session check
  useEffect(() => {
    const initializeAuth = async () => {
      // Check for magic link first
      await handleMagicLink();

      // Then check regular session if not already authenticated
      if (!authState.isAuthenticated) {
        await refreshAuth();
      }
    };

    initializeAuth();
  }, [authState.isAuthenticated, handleMagicLink, refreshAuth]); // Only run on mount

  // Listen for SuperTokens session events instead of polling
  useEffect(() => {
    const handleSessionEvents = () => {
      // SuperTokens doesn't have built-in events, so we use a more efficient check
      // Only check on page focus and specific intervals
      const handleVisibilityChange = () => {
        if (!document.hidden) {
          refreshAuth();
        }
      };

      document.addEventListener("visibilitychange", handleVisibilityChange);

      return () => {
        document.removeEventListener("visibilitychange", handleVisibilityChange);
      };
    };

    const cleanup = handleSessionEvents();
    return cleanup;
  }, [refreshAuth]);

  const contextValue: AuthContextType = {
    ...authState,
    signOut,
    refreshAuth,
    handleBackNavigation,
  };

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};
